import { createContext, useContext } from 'react';

/**
 * The wiring `FormField` computes and every control reads.
 *
 * It lives in its own module rather than beside the component because a file
 * that exports both a component and a hook cannot fast-refresh.
 */
export interface FieldContextValue {
  readonly controlId: string;
  readonly describedBy: string | undefined;
  readonly invalid: boolean;
  readonly required: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Returns `null` outside a `FormField`, so every control still works standalone
 * with its own `aria-label` — a search box in a toolbar has no visible label
 * and should not need a wrapper to be accessible.
 */
export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

/**
 * The attributes a control has to carry to be announced correctly. `own` values
 * win over the context, so a control can always override.
 */
export function fieldAttributes(
  context: FieldContextValue | null,
  own: {
    readonly id?: string;
    readonly 'aria-describedby'?: string;
    readonly 'aria-invalid'?: boolean;
    readonly required?: boolean;
  },
): {
  id: string | undefined;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  required: boolean | undefined;
} {
  const describedBy =
    own['aria-describedby'] ??
    (context?.describedBy !== undefined ? context.describedBy : undefined);
  const invalid = own['aria-invalid'] ?? context?.invalid ?? false;

  return {
    id: own.id ?? context?.controlId,
    'aria-describedby': describedBy,
    'aria-invalid': invalid ? true : undefined,
    required: own.required ?? context?.required,
  };
}
